/**
 * Theme Fly Cart JavaScript
 *
 * @package Theme
 * @version 1.0.0
 */

(function ($) {
	'use strict';

	/**
	 * Theme Fly Cart Class
	 */
	class ThemeFlyCart {
		constructor() {
			this.cart = null;
			this.overlay = null;
			this.toggle = null;
			this.isOpen = false;
			this.isLoading = false;
			this.lastUpdateTime = 0;
			this.updateTimeout = null;

			// Bind methods to maintain context
			this.init = this.init.bind(this);
			this.openCart = this.openCart.bind(this);
			this.closeCart = this.closeCart.bind(this);
			this.toggleCart = this.toggleCart.bind(this);
			this.handleKeyPress = this.handleKeyPress.bind(this);
			this.handleEscape = this.handleEscape.bind(this);
			this.updateQuantity = this.updateQuantity.bind(this);
			this.removeItem = this.removeItem.bind(this);
			this.trapFocus = this.trapFocus.bind(this);

			this.init();
		}

		/**
		 * Initialize the fly cart
		 */
		init() {
			// Cache DOM elements
			this.cart = document.getElementById('theme-fly-cart-area');
			this.overlay = this.cart?.querySelector('.fly-cart-overlay');

			this.toggleButtons = document.querySelectorAll(
				'#theme-fly-cart-toggle, #theme-fly-cart-toggle-desktop'
			);
			this.floatingIcon = document.getElementById(
				'theme-floating-cart-icon'
			);

			if (!this.cart || this.toggleButtons.length === 0) {
				return;
			}

			this.bindEvents();
			this.setupAccessibility();
		}

		/**
		 * Bind all event listeners
		 */
		bindEvents() {
			// Toggle cart
			this.toggleButtons.forEach((toggle) => {
				toggle.addEventListener('click', this.toggleCart);
				toggle.addEventListener('keydown', this.handleKeyPress);
			});

			// Floating icon toggle
			document.addEventListener('click', (e) => {
				if (e.target.closest('#theme-floating-cart-icon')) {
					e.preventDefault();
					this.toggleCart();
				}
			});

			document.addEventListener('keydown', (e) => {
				if (
					e.target.closest('#theme-floating-cart-icon') &&
					(e.key === 'Enter' || e.key === ' ')
				) {
					e.preventDefault();
					this.toggleCart();
				}
			});

			// Close cart
			const closeBtn = this.cart.querySelector('.fly-cart-close');
			const continueBtn = this.cart.querySelector('.fly-cart-continue');

			if (closeBtn) {
				closeBtn.addEventListener('click', this.closeCart);
			}

			if (continueBtn) {
				continueBtn.addEventListener('click', this.closeCart);
			}

			if (this.overlay) {
				this.overlay.addEventListener('click', this.closeCart);
			}

			// Quantity controls
			this.cart.addEventListener(
				'click',
				this.handleCartClick.bind(this)
			);
			this.cart.addEventListener(
				'change',
				this.handleQuantityChange.bind(this)
			);
			this.cart.addEventListener(
				'keydown',
				this.handleQuantityKeydown.bind(this)
			);

			// Global escape key
			document.addEventListener('keydown', this.handleEscape);

			// Update cart on WooCommerce events - debounced
			$(document.body).on(
				'added_to_cart',
				this.debouncedCartUpdate.bind(this)
			);
			$(document.body).on(
				'removed_from_cart',
				this.debouncedCartUpdate.bind(this)
			);

			// Disable WooCommerce fragments to prevent conflicts
			$(document.body).off('wc_fragments_loaded wc_fragments_refreshed');
		}

		/**
		 * Debounced cart update to prevent multiple rapid calls
		 */
		debouncedCartUpdate() {
			if (this.updateTimeout) {
				clearTimeout(this.updateTimeout);
			}

			this.updateTimeout = setTimeout(() => {
				this.updateCartCount();
			}, 100);
		}

		/**
		 * Setup accessibility features
		 */
		setupAccessibility() {
			this.cart.setAttribute('aria-hidden', 'true');

			this.toggleButtons.forEach((toggle) => {
				toggle.setAttribute('aria-expanded', 'false');
				toggle.setAttribute('type', 'button');
			});
		}

		/**
		 * Handle cart click events (delegation)
		 */
		handleCartClick(event) {
			const target = event.target;

			if (target.classList.contains('qty-plus')) {
				event.preventDefault();
				this.changeQuantity(target, 1);
			} else if (target.classList.contains('qty-minus')) {
				event.preventDefault();
				this.changeQuantity(target, -1);
			} else if (
				target.classList.contains('fly-cart-item-remove') ||
				target.closest('.fly-cart-item-remove')
			) {
				event.preventDefault();
				const removeBtn = target.classList.contains(
					'fly-cart-item-remove'
				)
					? target
					: target.closest('.fly-cart-item-remove');
				this.removeItem(removeBtn.dataset.key);
			}
		}

		/**
		 * Handle quantity input changes
		 */
		handleQuantityChange(event) {
			if (event.target.classList.contains('qty-input')) {
				const input = event.target;
				const newQuantity = parseInt(input.value, 10);

				if (newQuantity >= 1) {
					input.dataset.previousValue = input.value;
					this.updateQuantity(input.dataset.key, newQuantity);
				} else {
					input.value = 1;
				}
			}
		}

		/**
		 * Handle keyboard navigation in quantity inputs
		 */
		handleQuantityKeydown(event) {
			if (event.target.classList.contains('qty-input')) {
				if (event.key === 'ArrowUp') {
					event.preventDefault();
					this.changeQuantity(event.target, 1);
				} else if (event.key === 'ArrowDown') {
					event.preventDefault();
					this.changeQuantity(event.target, -1);
				}
			}
		}

		/**
		 * Change quantity by relative amount
		 */
		changeQuantity(element, delta) {
			const qtyContainer = element.closest('.fly-cart-item-quantity');
			const input = qtyContainer.querySelector('.qty-input');
			const currentQty = parseInt(input.value, 10);
			const newQty = Math.max(1, currentQty + delta);
			const maxQty = parseInt(input.getAttribute('max'), 10) || 999;

			if (newQty <= maxQty) {
				input.dataset.previousValue = input.value;
				input.value = newQty;
				this.updateQuantity(input.dataset.key, newQty);
			}
		}

		/**
		 * Handle key press events
		 */
		handleKeyPress(event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.toggleCart();
			}
		}

		/**
		 * Handle escape key
		 */
		handleEscape(event) {
			if (event.key === 'Escape' && this.isOpen) {
				this.closeCart();
			}
		}

		/**
		 * Open the cart
		 */
		openCart() {
			if (this.isOpen) return;

			this.isOpen = true;
			this.cart.classList.remove('fly-cart-hidden');
			this.cart.classList.add('fly-cart-visible');

			this.cart.setAttribute('aria-hidden', 'false');

			this.toggleButtons.forEach((toggle) => {
				toggle.setAttribute('aria-expanded', 'true');
			});

			document.body.style.overflow = 'hidden';
			this.trapFocus();

			setTimeout(() => {
				const closeBtn = this.cart.querySelector('.fly-cart-close');
				if (closeBtn) {
					closeBtn.focus();
				}
			}, 100);

			this.announceToScreenReader(
				themeFlyCartAjax.i18n.cart +
					' ' +
					themeFlyCartAjax.i18n.openCart
			);
		}

		/**
		 * Close the cart
		 */
		closeCart() {
			if (!this.isOpen) return;

			this.isOpen = false;
			this.cart.classList.remove('fly-cart-visible');
			this.cart.classList.add('fly-cart-hidden');

			this.cart.setAttribute('aria-hidden', 'true');

			// Update aria-expanded for ALL toggle buttons
			this.toggleButtons.forEach((toggle) => {
				toggle.setAttribute('aria-expanded', 'false');
			});

			document.body.style.overflow = '';

			// Focus the first available toggle button
			if (this.toggleButtons[0]) {
				this.toggleButtons[0].focus();
			}

			this.announceToScreenReader(themeFlyCartAjax.i18n.closeCart);
		}

		/**
		 * Toggle cart open/close
		 */
		toggleCart() {
			if (this.isOpen) {
				this.closeCart();
			} else {
				this.openCart();
			}
		}

		/**
		 * Trap focus within the cart when open
		 */
		trapFocus() {
			if (!this.isOpen) return;

			const focusableElements = this.cart.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];

			const handleTabKey = (event) => {
				if (event.key !== 'Tab') return;

				if (event.shiftKey) {
					if (document.activeElement === firstElement) {
						event.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement) {
						event.preventDefault();
						firstElement.focus();
					}
				}
			};

			this.cart.removeEventListener('keydown', this.currentFocusTrap);
			this.currentFocusTrap = handleTabKey;
			this.cart.addEventListener('keydown', this.currentFocusTrap);
		}

		/**
		 * Update cart item quantity via AJAX
		 */
		updateQuantity(cartItemKey, quantity) {
			if (this.isLoading) return;

			this.isLoading = true;

			const data = {
				action: 'theme_update_cart_item',
				cart_item_key: cartItemKey,
				quantity: quantity,
				nonce: themeFlyCartAjax.nonce,
			};

			// Disable WooCommerce fragments during our update
			$(document.body).off('wc_fragments_loaded wc_fragments_refreshed');

			$.post(themeFlyCartAjax.ajaxUrl, data)
				.done((response) => {
					if (response.success) {
						this.updateCartUI(response.data);
						this.announceToScreenReader(
							themeFlyCartAjax.i18n.quantity + ' ' + quantity
						);
					} else {
						this.showError(
							response.data?.message ||
								themeFlyCartAjax.i18n.error
						);
						this.revertQuantityInput(cartItemKey);
					}
				})
				.fail(() => {
					this.showError(themeFlyCartAjax.i18n.error);
					this.revertQuantityInput(cartItemKey);
				})
				.always(() => {
					this.isLoading = false;
				});
		}

		/**
		 * Remove item from cart via AJAX
		 */
		removeItem(cartItemKey) {
			if (this.isLoading) return;

			const cartItem = this.cart
				.querySelector(`[data-key="${cartItemKey}"]`)
				.closest('.fly-cart-item');
			this.setLoading(true, cartItem);

			const data = {
				action: 'theme_remove_cart_item',
				cart_item_key: cartItemKey,
				nonce: themeFlyCartAjax.nonce,
			};

			$.post(themeFlyCartAjax.ajaxUrl, data)
				.done((response) => {
					if (response.success) {
						if (cartItem) {
							cartItem.style.transition =
								'opacity 0.3s ease, transform 0.3s ease';
							cartItem.style.opacity = '0';
							cartItem.style.transform = 'translateX(100%)';

							setTimeout(() => {
								this.updateCartUI(response.data);
								this.announceToScreenReader(
									themeFlyCartAjax.i18n.removeFromCart
								);
							}, 300);
						} else {
							this.updateCartUI(response.data);
							this.announceToScreenReader(
								themeFlyCartAjax.i18n.removeFromCart
							);
						}
					} else {
						this.showError(
							response.data?.message ||
								themeFlyCartAjax.i18n.error
						);
					}
				})
				.fail(() => {
					this.showError(themeFlyCartAjax.i18n.error);
				})
				.always(() => {
					this.setLoading(false, cartItem);
				});
		}

		/**
		 * Update cart UI after AJAX response
		 */
		updateCartUI(data) {
			const now = Date.now();

			// Prevent duplicate calls
			if (now - this.lastUpdateTime < 500) {
				return;
			}

			this.lastUpdateTime = now;

			// Always update cart count and total
			this.updateCartCount(data.cart_count);

			const totalElement = this.cart.querySelector('.total-amount');
			if (totalElement && data.cart_total) {
				totalElement.innerHTML = data.cart_total;
			}

			// Update fees if available
			const feesContainer = this.cart.querySelector(
				'.fly-cart-fees-container'
			);
			if (feesContainer && data.fees_html !== undefined) {
				feesContainer.innerHTML = data.fees_html;
			}

			// Handle different update scenarios
			if (data.full_update === true) {
				const itemsContainer =
					this.cart.querySelector('.fly-cart-items');
				if (itemsContainer && data.cart_html) {
					itemsContainer.innerHTML = data.cart_html;
				}

				if (this.isOpen) {
					setTimeout(() => this.trapFocus(), 50);
				}
			} else if (data.updated_item && data.cart_item_key) {
				this.updateSpecificItem(data.cart_item_key, data.updated_item);
			}
		}

		/**
		 * Update specific cart item without causing visual blinks
		 */
		updateSpecificItem(cartItemKey, itemData) {
			const cartItem = this.cart.querySelector(
				`[data-key="${cartItemKey}"]`
			);
			if (!cartItem) return;

			const cartItemContainer = cartItem.closest('.fly-cart-item');
			if (!cartItemContainer) return;

			let hasChanges = false;

			// Check if quantity actually needs updating
			const qtyInput = cartItemContainer.querySelector('.qty-input');
			if (qtyInput && itemData.quantity !== undefined) {
				const currentValue = parseInt(qtyInput.value, 10);
				const newValue = parseInt(itemData.quantity, 10);

				if (currentValue !== newValue) {
					hasChanges = true;
				}
			}

			// Check if price actually needs updating
			const priceElement = cartItemContainer.querySelector(
				'.fly-cart-item-price'
			);
			if (priceElement && itemData.line_total) {
				const currentPrice = this.normalizePrice(
					priceElement.innerHTML
				);
				const newPrice = this.normalizePrice(itemData.line_total);

				if (currentPrice !== newPrice) {
					hasChanges = true;
				}
			}

			// Only update if there are actual changes
			if (!hasChanges) {
				return;
			}

			// Store current focused element
			const currentFocus = document.activeElement;
			const wasFocused = cartItemContainer.contains(currentFocus);

			// Apply quantity update if needed
			if (qtyInput && itemData.quantity !== undefined) {
				const currentValue = parseInt(qtyInput.value, 10);
				const newValue = parseInt(itemData.quantity, 10);

				if (currentValue !== newValue) {
					qtyInput.value = newValue;
				}

				if (itemData.max_quantity) {
					qtyInput.setAttribute('max', itemData.max_quantity);
				}
			}

			// Apply price update if needed
			if (priceElement && itemData.line_total) {
				const currentPrice = this.normalizePrice(
					priceElement.innerHTML
				);
				const newPrice = this.normalizePrice(itemData.line_total);

				if (currentPrice !== newPrice) {
					requestAnimationFrame(() => {
						priceElement.innerHTML = itemData.line_total;
					});
				}
			}

			// Restore focus
			if (
				wasFocused &&
				currentFocus &&
				cartItemContainer.contains(currentFocus)
			) {
				setTimeout(() => {
					currentFocus.focus();
				}, 10);
			}
		}

		/**
		 * Normalize price strings for accurate comparison
		 */
		normalizePrice(priceHtml) {
			if (!priceHtml) return '';

			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = priceHtml;

			return tempDiv.textContent.replace(/\s+/g, ' ').trim();
		}

		/**
		 * Update cart count in header and fly cart
		 */
		updateCartCount(count) {
			const countElements = document.querySelectorAll(
				'.fly-cart-count, .header-cart-count, .cart-count-badge'
			);
			countElements.forEach((el) => {
				el.textContent = count || '0';
				el.style.display = '';
			});

			// Update floating cart icon
			const floatingCountElement = document.getElementById(
				'floating-cart-count-number'
			);
			if (floatingCountElement) {
				floatingCountElement.textContent = count || '0';
			}

			// Update floating icon visibility and data attribute
			if (this.floatingIcon) {
				this.floatingIcon.setAttribute('data-count', count || '0');

				// Handle hide when empty functionality
				if (
					this.floatingIcon.classList.contains(
						'floating-cart-hide-empty'
					)
				) {
					if (count === 0 || count === '0') {
						this.floatingIcon.classList.add('floating-cart-hidden');
						this.floatingIcon.setAttribute('aria-hidden', 'true');
						this.floatingIcon.setAttribute('tabindex', '-1');
					} else {
						this.floatingIcon.classList.remove(
							'floating-cart-hidden'
						);
						this.floatingIcon.setAttribute('aria-hidden', 'false');
						this.floatingIcon.setAttribute('tabindex', '0');
					}
				}

				// Update aria-label - FIX: Check if i18n properties exist
				let label;
				if (count > 0) {
					// Check if openCartWithCount exists and has the replace method
					if (
						themeFlyCartAjax.i18n &&
						themeFlyCartAjax.i18n.openCartWithCount
					) {
						label = themeFlyCartAjax.i18n.openCartWithCount.replace(
							'%d',
							count
						);
					} else {
						// Fallback if translation is missing
						label = `Open cart (${count} items)`;
					}
				} else {
					// Check if openCart exists
					if (
						themeFlyCartAjax.i18n &&
						themeFlyCartAjax.i18n.openCart
					) {
						label = themeFlyCartAjax.i18n.openCart;
					} else {
						// Fallback if translation is missing
						label = 'Open cart';
					}
				}
				this.floatingIcon.setAttribute('aria-label', label);
			}
		}

		/**
		 * Handle WooCommerce cart update events
		 */
		handleWooCommerceAddToCart() {
			// Auto-open cart when item is added
			this.openCart();

			// Add bounce animation to floating icon if visible
			if (
				this.floatingIcon &&
				!this.floatingIcon.classList.contains('floating-cart-hidden')
			) {
				this.floatingIcon.classList.add('floating-cart-bounce');
				setTimeout(() => {
					this.floatingIcon.classList.remove('floating-cart-bounce');
				}, 600);
			}
		}

		/**
		 * Set loading state (only for removals, not quantity updates)
		 */
		setLoading(loading, specificItem = null) {
			this.isLoading = loading;

			if (loading) {
				this.cart.classList.add('fly-cart-loading');
				if (specificItem) {
					specificItem.classList.add('fly-cart-item-loading');
				}
			} else {
				this.cart.classList.remove('fly-cart-loading');
				if (specificItem) {
					specificItem.classList.remove('fly-cart-item-loading');
				}
				this.cart
					.querySelectorAll('.fly-cart-item-loading')
					.forEach((item) => {
						item.classList.remove('fly-cart-item-loading');
					});
			}
		}

		/**
		 * Revert quantity input to previous value
		 */
		revertQuantityInput(cartItemKey) {
			const input = this.cart.querySelector(
				`[data-key="${cartItemKey}"]`
			);
			if (input && input.dataset.previousValue) {
				input.value = input.dataset.previousValue;
			}
		}

		/**
		 * Show error message
		 */
		showError(message) {
			const errorDiv = document.createElement('div');
			errorDiv.className = 'fly-cart-error';
			errorDiv.textContent = message;
			errorDiv.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #dc3545;
                color: white;
                padding: 10px 15px;
                border-radius: 4px;
                z-index: 1000000;
                box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                font-size: 14px;
                max-width: 300px;
                word-wrap: break-word;
            `;

			document.body.appendChild(errorDiv);

			setTimeout(() => {
				if (errorDiv.parentNode) {
					errorDiv.parentNode.removeChild(errorDiv);
				}
			}, 3000);

			this.announceToScreenReader(message);
		}

		/**
		 * Announce messages to screen readers
		 */
		announceToScreenReader(message) {
			const announcement = document.createElement('div');
			announcement.setAttribute('aria-live', 'polite');
			announcement.setAttribute('aria-atomic', 'true');
			announcement.className = 'screen-reader-text';
			announcement.style.cssText = `
                position: absolute !important;
                width: 1px !important;
                height: 1px !important;
                padding: 0 !important;
                margin: -1px !important;
                overflow: hidden !important;
                clip: rect(0, 0, 0, 0) !important;
                white-space: nowrap !important;
                border: 0 !important;
            `;
			announcement.textContent = message;

			document.body.appendChild(announcement);

			setTimeout(() => {
				if (announcement.parentNode) {
					announcement.parentNode.removeChild(announcement);
				}
			}, 1000);
		}
	}

	/**
	 * Initialize when DOM is ready
	 */
	$(document).ready(function () {
		if (typeof themeFlyCartAjax !== 'undefined') {
			window.themeFlyCartInstance = new ThemeFlyCart();
		}
	});

	// Listen for WooCommerce add to cart events
	$(document.body).on('added_to_cart', function () {
		if (window.themeFlyCartInstance) {
			window.themeFlyCartInstance.handleWooCommerceAddToCart();
		}
	});

	window.ThemeFlyCart = ThemeFlyCart;
})(jQuery);
